import crioTheme from '@crio/crio-react-component/dist/cjs/config/crioTheme';
import { faForward, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, ThemeProvider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleText, InfoText } from '..';
import ProcedureContext from '../../context/ProcedureContext';
import { isAnySandboxMode, ProcedureStatus, VisitReferenceType } from '../../enums';
import { InstructionContent } from '../../types';
import { SkipProcedureDialog } from './Dialog';

const StyledInfoText = styled(InfoText)`
  .InstructionsHeader {
    justify-content: space-between;
    padding-bottom: 22px
  }
  h3 {
    color: ${(props) => props.theme.palette.h.main};
    margin: 0;
    font-size: 1.45em;
    font-weight: 500;
    font-size: 16px;
  }

  ul.InstructionBodyItems {
    padding: 0;
    font-weight: 500;
    font-size: 14px;
    margin: 0;
  }
  
  .InstructionBodyItemsContainer {
    display: flex;
    gap: 8px;
    padding-top: 15px;
    padding-bottom: 15px;
  }

  .InstructionBodyItemsContainer .InfoCircleIcon {
    margin-top: 2px;
    color: #009ceb;
    height: 18px;
  }

  ul.InstructionBodyItems.single-item {
    list-style: none;
  }

  ul.InstructionBodyItems.multiple-items {
    list-style: disc;
    padding-left: 20px;
  }

  span {
    margin-top: 1em;
  }

  span.ReferencedFrom ul {
    margin-top: 0;
    margin-bottom: 0;
    padding-inline-start: 20px;
  }

  span.MuiTypography-root {
    font-size: 1em;
  }
`;

const SkipProcedureButton = styled(InfoText)`
  padding: 5px 20px;
  background-color: #eceade;

  &.Disabled {
    pointer-events: none;

    p.MuiTypography-root {
      opacity: .6;
    }
  }

  &:hover {
    cursor: pointer;

    p.MuiTypography-root {
      color: ${(props) => props.theme.palette.linkText.main};
      text-decoration: underline;
    }
  }

  p.MuiTypography-root {
    font-size: 1em;
    color: ${(props) => props.theme.palette.grey[600]};
  }

  .FastForwardIcon {
    margin-right: 5px;
  }
`;

export type InstructionTextProps = {
  instructions: InstructionContent;
  header?: JSX.Element;
  allowSkip: boolean;
  procedureStatus?: ProcedureStatus;
  readOnly: boolean;
  truncate: boolean;
};

interface SkipProcedureDialogInterface {
  isOpen: boolean,
  continueHandler?: Function,
}

function InstructionText({
  instructions, header, allowSkip, readOnly, procedureStatus, truncate,
}: InstructionTextProps) {
  const {
    isLoading, saveProcedure, proceedToNextProcedure, visitMode,
  } = useContext(ProcedureContext);
  const { body: instructionsBody, bodyItems, questionCriteria } = instructions || {};

  const [skipProcedureDialogValues, setSkipProcedureDialogValues] = useState<SkipProcedureDialogInterface>({ isOpen: false });
  const { t } = useTranslation();
  const confirmSkipProcedure = () => {
    setSkipProcedureDialogValues({
      isOpen: true,
    });
  };

  const closeSkipProcedureConfirmation = () => {
    setSkipProcedureDialogValues((prevState) => ({ ...prevState, isOpen: false }));
  };

  if (isLoading) return null;

  let referencedVisitText;
  let referencedProcedureText;
  let referencedQuestionText;
  if (questionCriteria) {
    if (isAnySandboxMode(visitMode)) {
      if (questionCriteria.visitReferenceType === VisitReferenceType.CURRENT) {
        referencedVisitText = t('Procedure.Question.Reference.Referenced Current Visit');
      } else if (questionCriteria.visitReferenceType === VisitReferenceType.PREVIOUS) {
        referencedVisitText = t('Procedure.Question.Reference.Referenced Previous Visit');
      } else {
        referencedVisitText = t('Procedure.Question.Reference.Referenced Static Visit', { visitNumber: questionCriteria.visitNumber, visitName: questionCriteria.visitName });
      }
    } else {
      referencedVisitText = !questionCriteria.visitFound
        ? t('Procedure.Question.Reference.Cannot find referenced visit')
        : t('Procedure.Question.Reference.Referenced Static Visit', { visitNumber: questionCriteria.visitNumber, visitName: questionCriteria.visitName });
    }

    referencedProcedureText = !questionCriteria.procedureFound
      ? t('Procedure.Question.Reference.Cannot find referenced procedure')
      : t('Procedure.Question.Reference.Referenced Procedure', { procedureName: questionCriteria.procedureName });

    referencedQuestionText = !questionCriteria.questionFound
      ? t('Procedure.Question.Reference.Cannot find referenced question')
      : t('Procedure.Question.Reference.Referenced Question', { questionText: questionCriteria.questionText });
  }

  return (
    <ThemeProvider theme={crioTheme}>
      {(instructionsBody || bodyItems || questionCriteria)
        && (
          <StyledInfoText className="Instruction">
            {instructionsBody
              && (
                <>
                  {header}
                  <CollapsibleText
                    data-testid="instructionText"
                    text={instructionsBody}
                    numLines={truncate ? 3 : undefined}
                  />
                </>
              )}
            {
                bodyItems && bodyItems.length > 0
                && (
                <div className="InstructionBodyItemsContainer">
                  <FontAwesomeIcon className="InfoCircleIcon" icon={faInfoCircle} />
                  <ul className={`InstructionBodyItems ${bodyItems.length === 1 ? 'single-item' : 'multiple-items'}`}>
                    {bodyItems?.map((text, index) => (
                      <li
                        key={index}
                        data-testid={text === t('Procedure.Traits Data Message') ? 'ndc-traits-data-message' : undefined}
                      >
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                )
            }
            {!!questionCriteria
              && (
              <Typography component="span" className="ReferencedFrom">
                {t('Procedure.Question.Reference.Referenced From')}
                <ul>
                  <li>{referencedVisitText}</li>
                  <li>{referencedProcedureText}</li>
                  <li>{referencedQuestionText}</li>
                </ul>
              </Typography>
              )}
          </StyledInfoText>
        )}
      {
        (allowSkip && !procedureStatus)
        && (
          <div data-qa-id="SkipButton">
            <SkipProcedureButton className={`SkipButtonBody ${readOnly ? 'Disabled' : ''}`} onClick={confirmSkipProcedure}>
              <Typography>
                <FontAwesomeIcon className="FastForwardIcon" icon={faForward} />
                {t('Procedure.Dialog.SkipProcedure.Skip Procedure')}
              </Typography>
            </SkipProcedureButton>
            <SkipProcedureDialog
              {...skipProcedureDialogValues}
              closeHandler={closeSkipProcedureConfirmation}
              continueHandler={() => {
                saveProcedure({
                  skipMissingQuestionsCheck: true,
                  skipProcedure: true,
                  successCallback: proceedToNextProcedure,
                });
              }}
            />
          </div>
        )
      }
    </ThemeProvider>
  );
}

InstructionText.defaultProps = {
  header: <div />,
  procedureStatus: undefined,
};

export default InstructionText;
